import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useCallback, useEffect, useRef } from 'react'
import { setStaticImageOverride } from '../../../../state/state.timers'
import { useUserSelector } from '../../../Hooks/useUser'
import PreviewPosterImage from '../../PreviewPosterImage'

const BackgroundFadeContainer = styled('div')({
    // zIndex: 999999999999999,
    position: 'absolute',
    overflow: 'hidden',
})

const displayNone = css({ display: 'none' })

type BackgroundFadeProps = {
    id: number
    width: number
    height: number
    filter: string
    top?: number
    left?: number
    disabled?: boolean
}
const FRAME_THRESHOLD_MS = 200

const BackgroundFade = memo((props: BackgroundFadeProps) => {
    const {
        id,
        width,
        height,
        top = 0,
        left = 0,
        filter,
        disabled = false,
    } = props

    const banned = useUserSelector(id, 'banned')
    const isKicked = useUserSelector(id, 'isKicked')
    const isOffline = useUserSelector(id, 'isOffline')
    const isAway = useUserSelector(id, 'isAway')
    const isClubShow = useUserSelector(id, 'isClubShow')
    const isGroupShow = useUserSelector(id, 'isGroupShow')
    const isPrivateShow = useUserSelector(id, 'isPrivateShow')
    const isTruePrivate = useUserSelector(id, 'isTruePrivate')

    const canvasRef = useRef<HTMLCanvasElement>(undefined)
    const snapRef = useRef<HTMLImageElement>(undefined)
    const animationRef = useRef<number>(undefined)

    const containerCss = {
        top,
        left,
        filter,
    }
    const frameTimerRef = useRef(0)
    const shouldStop =
        disabled ||
        isKicked ||
        banned ||
        isOffline ||
        isAway ||
        isClubShow ||
        isGroupShow ||
        isPrivateShow ||
        isTruePrivate

    const videoCallback = useCallback(
        (now) => {
            const timelapse = now - frameTimerRef.current
            if (timelapse > FRAME_THRESHOLD_MS && !!canvasRef.current) {
                const ctx = canvasRef.current.getContext('2d')
                ctx.globalAlpha = 0.1

                const imageEl = snapRef.current
                if (imageEl && imageEl.complete) {
                    // on the off chance this image is busted
                    try {
                        ctx.drawImage(imageEl, 0, 0, width, height)
                    } catch {
                        return
                    }
                }
                frameTimerRef.current = now
            }
            animationRef.current = requestAnimationFrame(videoCallback)
        },
        [id, height, width, left],
    )
    const onLoad = useCallback((event) => {
        snapRef.current = event.target as HTMLImageElement
    }, [])

    useEffect(() => {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
        if (!shouldStop && !!id) {
            setStaticImageOverride(id)
            animationRef.current = requestAnimationFrame(videoCallback)
            return () => cancelAnimationFrame(animationRef.current)
        }
    }, [id, videoCallback, shouldStop, disabled])

    if (!id || shouldStop) {
        canvasRef.current = null
        return null
    }

    return (
        <BackgroundFadeContainer id="background-fade" css={containerCss}>
            <canvas ref={canvasRef} width={width} height={height} />
            <div css={displayNone}>
                <PreviewPosterImage
                    id={id}
                    onLoad={onLoad}
                    setDimensions={false}
                    staticImage={shouldStop}
                    small
                    compact
                />
            </div>
        </BackgroundFadeContainer>
    )
})

export default BackgroundFade
